import React from 'react';
import { Modal, Textarea, InputBase, Loading } from '@brighterion/br-ui';
import { Field, FieldProps, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useTranslation, Trans } from 'react-i18next';
import useAlertBanner from 'Hooks/reduxHelperHooks/useAlertBanner';
import useGetMerchantOverrides from 'Hooks/query-hooks/merchants/useGetMerchantOverrides';
import useGetLandlordInfo from 'Hooks/query-hooks/tenant/useGetLandlordInfo';
import useGetTenantGroupOverrideRules from 'Hooks/query-hooks/tenant/useGetTenantGroupOverrideRules';
import useDeleteRule from 'Hooks/query-hooks/global-rules/useDeleteRule';
import { useHistory } from 'react-router-dom';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import { useQueryClient } from '@tanstack/react-query';
import { trackEvent, EventTypes, EventNames } from '@brighterion/insights';
import {
    RQ_ALL_LIST,
    RQ_GLOBAL_RULES,
    RQ_HISTORY,
    RQ_LIST_BY_ID,
    RQ_PARAMETERS,
    RQ_RULE_GROUPS,
    RQ_RULE_REVISION_MERCHANT_OVERRIDES,
    RQ_RULE_REVISION_TENANT_GROUP_OVERRIDES
} from 'Utils';
import styles from './DeleteRuleModal.css';
import { DeleteRuleErrorModal } from './DeleteRuleErrorModal';

type OverrideSubject = 'merchant' | 'tenantGroup';

type Props = {
    templateRepositoryId: number;
    isOpen: boolean;
    closeModal: () => void;
    ruleId: string;
    isRuleDeployed: boolean;
};

const DeleteRuleModal = ({ closeModal, isOpen, ruleId, templateRepositoryId, isRuleDeployed }: Props) => {
    const history = useHistory();
    const { data: merchantOverrides } = useGetMerchantOverrides();
    const { addModal } = useReduxModal();
    const { data: landlordInfo } = useGetLandlordInfo();
    const isLandlord = !!landlordInfo?.isLandlord;
    const overrideSubject: OverrideSubject = isLandlord ? 'tenantGroup' : 'merchant';
    const { data: tenantGroupOverrides } = useGetTenantGroupOverrideRules(overrideSubject === 'tenantGroup');
    const overrideTenantGroupIds = tenantGroupOverrides && ruleId ? tenantGroupOverrides[ruleId] || [] : [];
    const overrideMerchantIds = merchantOverrides && ruleId ? merchantOverrides[ruleId] || [] : [];
    const overrideSubjectIds = isLandlord ? overrideTenantGroupIds : overrideMerchantIds;

    const subjectName = isLandlord ? 'Tenant Group' : 'Merchant';

    const { t } = useTranslation();
    const { addAlert } = useAlertBanner();
    const modalTitle = t('Error Deleting Rule');
    const queryClient = useQueryClient();

    const [isLoading, setIsLoading] = React.useState(false);
    const [errorModalOpen, setErrorModalOpen] = React.useState(false);
    const [comment, setComment] = React.useState('');

    const queryInvalidateFunction = () => {
        queryClient.invalidateQueries([RQ_GLOBAL_RULES, templateRepositoryId]);
        queryClient.invalidateQueries([RQ_RULE_GROUPS]);
        queryClient.invalidateQueries([RQ_HISTORY]);
        queryClient.invalidateQueries([RQ_ALL_LIST]);
        queryClient.invalidateQueries([RQ_LIST_BY_ID]);
        queryClient.invalidateQueries([RQ_PARAMETERS]);
    };
    const defaultErrorHandler = () => {
        queryClient.invalidateQueries([RQ_GLOBAL_RULES, templateRepositoryId, ruleId]);
        queryClient.invalidateQueries([RQ_RULE_REVISION_TENANT_GROUP_OVERRIDES]);
        queryClient.invalidateQueries([RQ_RULE_REVISION_MERCHANT_OVERRIDES]);
        queryClient.invalidateQueries([RQ_HISTORY]);
        queryClient.invalidateQueries([RQ_PARAMETERS]);
    };

    const onSuccess = () => {
        setIsLoading(false);
        closeModal();
        if (isRuleDeployed) {
            addAlert({
                id: 'delete-rule',
                content: <Trans i18nKey='Rule Ready To Delete'> {{ ruleId }} </Trans>,
                status: 'success'
            });
        } else {
            addAlert({
                id: 'delete-rule',
                content: <Trans i18nKey='Rule Was Deleted'> {{ ruleId }} </Trans>,
                status: 'success'
            });
        }
        history.replace('/global-rules'); // handeling inconsistent browser state when rule gets deleted
    };

    const onError = (err: any) => {
        closeModal();
        // Concurrency flow A: Rule could not be deleted since it was deleted by another user
        const handleNotFound = () => {
            addModal({
                id: 'delete-rule-error',
                status: 'warning',
                title: modalTitle,
                content: t('Rule Another User Deleted Error Message'),
                showCloseIcon: false,
                showCloseButton: false,
                closeOnAction: true,
                onActionText: 'Confirm',
                onClose: () => {
                    queryInvalidateFunction();
                    history.replace('/global-rules');
                }
            });
        };
        // Concurrency flow B: Rule could not be deleted since it has been deployed and allow overrides set to yes
        const handleConflict = () => {
            addModal({
                id: 'delete-deployed-rule-error',
                status: 'warning',
                title: modalTitle,
                content: t('Rule deployed and Allow Tenant overrides Yes Error Message'),
                showCloseIcon: false,
                showCloseButton: false,
                closeOnAction: true,
                onActionText: 'Confirm',
                onClose: () => {
                    defaultErrorHandler();
                }
            });
        };
        // Technical or backend generic error
        const handleDefault = () => {
            setErrorModalOpen(true);
        };

        switch (err?.response?.status) {
            case 404:
                handleNotFound();
                break;
            case 409:
                handleConflict();
                break;
            default:
                handleDefault();
                break;
        }
    };

    const mutation = useDeleteRule({
        templateRepositoryId,
        mutationOptions: {
            onSuccess,
            onError
        }
    });

    const initialValues = {
        comment: '',
        ruleid: ''
    };

    const tenantCount = overrideSubjectIds.length;

    const overrideText = tenantCount > 1 ? 'overrides' : 'override';

    const DeleteRuleSchema = Yup.object().shape({
        comment: Yup.string().optional().max(150).trim(),
        ruleid: Yup.string()
            .required(() => t('IncorrectRuleID'))
            .max(20)
            .trim()
            .test('ruleIdCheck', t('IncorrectRuleID'), (value) =>
                value !== undefined ? value.toLowerCase() === ruleId.toLowerCase() : true
            )
    });

    return (
        <>
            <Modal
                isOpen={isOpen}
                closeModal={closeModal}
                containerClass={styles.modalContainer}
                status='warning'
                dataTestId='delete-rule-modal'
                closeOnClickOutside={false}
            >
                <Modal.Title
                    id='delete-rule-modal-title'
                    title={t('Delete Rule Modal Title', {
                        ruleId
                    })}
                    onClose={closeModal}
                    showCloseIcon
                    statusIcon='warning'
                    isSubmitting={isLoading}
                />
                <div className={styles.deleteConfirmationContent}>
                    <div className={styles.deleteRuleInfo}>{t('Delete Rule Modal Confirmation')}</div>
                    {isRuleDeployed ? (
                        <div className={styles.deleteRuleInfo}>{t('Delete Rule Modal Confirmation Deployment')}</div>
                    ) : null}
                    <div className={styles.deleteRuleInfo}>{t('Delete Rule Modal Confirmation RuleID')}</div>
                </div>
                <Formik
                    enableReinitialize
                    initialValues={initialValues}
                    validationSchema={DeleteRuleSchema}
                    validateOnChange={false}
                    validateOnBlur={false}
                    onSubmit={(values) => {
                        setIsLoading(true);
                        setComment(values.comment);
                        mutation.mutate({ ruleId, comment: values.comment });
                        trackEvent({
                            eventType: EventTypes.track,
                            eventName: EventNames.click,
                            payload: {
                                clickIdentifier: 'Global Rules - Delete Rule',
                                text: 'Delete Rule'
                            }
                        });
                    }}
                >
                    {({ handleChange, handleBlur, handleSubmit, setFieldError }) => {
                        const handleFocusField = (e: { target: { name: any } }) => {
                            setFieldError(e.target.name, undefined);
                        };
                        return (
                            <Form>
                                <Field name='ruleid'>
                                    {({ field, form: { errors } }: FieldProps) => (
                                        <InputBase
                                            className={styles.leftText}
                                            size='sm'
                                            type='text'
                                            name='ruleid'
                                            label={t('Rule ID')}
                                            value={field.value}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            onFocus={handleFocusField}
                                            disabled={false}
                                            error={!!errors[field.name]}
                                            errorMsg={errors[field.name]?.toString()}
                                            allowPan
                                        />
                                    )}
                                </Field>
                                <div className={styles.content}>
                                    <Field name='comment'>
                                        {({ field, form: { errors } }: FieldProps) => {
                                            return (
                                                <Textarea
                                                    className={styles.textarea}
                                                    label={t('Comment (Optional)')}
                                                    lineNum={3}
                                                    showCounter={false}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    maxChar={150}
                                                    value={field.value}
                                                    name='comment'
                                                    error={!!errors[field.name]}
                                                    errorMsg={errors[field.name]?.toString()}
                                                    allowPan
                                                />
                                            );
                                        }}
                                    </Field>
                                    {overrideSubjectIds.length ? (
                                        <div className={styles.notification}>
                                            <Trans i18nKey='DeleteRuleModalSubjectOverrides'>
                                                {{ tenantCount }} {{ subjectName }} {{ overrideText }}
                                            </Trans>
                                        </div>
                                    ) : null}
                                </div>
                                <Modal.Footer
                                    id='delete-rule-modal-footer'
                                    onClose={closeModal}
                                    disableOnCancel={mutation.isLoading}
                                    disableOnAction={mutation.isLoading}
                                    onAction={handleSubmit}
                                    onActionText={
                                        mutation.isLoading ? <Loading size='sm' fillName='white' /> : t('Delete Rule')
                                    }
                                    onCloseText={t('Cancel')}
                                />
                            </Form>
                        );
                    }}
                </Formik>
            </Modal>
            <DeleteRuleErrorModal
                isOpen={errorModalOpen}
                closeModal={() => {
                    setErrorModalOpen(false);
                    defaultErrorHandler();
                }}
                confirmDeleteRule={() => {
                    mutation.mutate({ ruleId, comment });
                }}
                title={modalTitle}
                content={t('Delete Rule Technical Error Message')}
                onActionText={mutation.isLoading ? <Loading size='sm' fillName='white' /> : t('Try Again')}
                disableOnCancel={mutation.isLoading}
                disableOnAction={mutation.isLoading}
            />
        </>
    );
};

export default DeleteRuleModal;
